import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { useAITTheme, type AITTheme } from '@this/shared/ui/theme';
import { Modal, ModalBody, ModalHeader } from '@this/src/components/shared/ui/feedbacks/modal';
import { Loading } from '@this/src/components/shared/ui/feedbacks/loading';
import MemberAPI from '@this/src/api/arrangement/member_api';
import type { MemberInfoResponseArgs } from '@this/src/api/arrangement/member_api';

interface Props {
  show: boolean;
  organizationId: number | null;
  userId: number | null;
  onClose: () => void;
}

const MemberInfoModal: React.FC<Props> = ({ show, organizationId, userId, onClose }) => {
  const { theme } = useAITTheme();
  const styles = useMemo(() => getStyles(theme), [theme]);
  const [loading, setLoading] = useState(false);
  const [member, setMember] = useState<MemberInfoResponseArgs | null>(null);
  const [error, setError] = useState<string | null>(null);

  const fetchMember = useCallback(async () => {
    if (!organizationId || !userId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await MemberAPI.show(organizationId, userId);
      setMember(res);
    } catch (e) {
      setError('社員情報の取得に失敗しました');
    } finally {
      setLoading(false);
    }
  }, [organizationId, userId]);

  useEffect(() => {
    if (show && organizationId && userId) {
      fetchMember();
    }
    if (!show) {
      setMember(null);
      setError(null);
    }
  }, [show, organizationId, userId, fetchMember]);

  if (!show) return null;

  const hasUser = organizationId != null && userId != null;

  return (
    <Modal open={show} onClose={onClose} size="large">
      <ModalHeader>社員情報</ModalHeader>
      <ModalBody style={styles.body}>
        {!hasUser && <p style={styles.emptyMessage}>社員情報が登録されていません</p>}
        {hasUser && loading && <Loading />}
        {hasUser && error && <p style={styles.errorMessage}>{error}</p>}
        {hasUser && !loading && !error && member && (
          <>
            <section style={styles.section}>
              <h3 style={styles.sectionTitle}>基本情報</h3>
              <ul style={styles.list}>
                <li>社員番号: {member.employee_number || ''}</li>
                <li>
                  氏名: {member.last_name || ''} {member.first_name || ''}
                </li>
                <li>
                  氏名（カナ）: {member.last_name_kana || ''} {member.first_name_kana || ''}
                </li>
                <li>
                  氏名（ローマ字）: {member.last_name_roman || ''} {member.first_name_roman || ''}
                </li>
                <li>性別: {member.gender || ''}</li>
                <li>誕生日: {member.birthday || ''}</li>
                <li>メールアドレス: {member.email || ''}</li>
                <li>電話番号: {member.tel || ''}</li>
              </ul>
            </section>

            <section style={styles.section}>
              <h3 style={styles.sectionTitle}>所属</h3>
              <ul style={styles.list}>
                <li>拠点: {member.organization_base?.name || ''}</li>
                <li>部署: {member.department?.name || ''}</li>
                <li>役職等級: {member.grade?.name || ''}</li>
                <li>役割: {member.organization_role?.name || ''}</li>
              </ul>
            </section>

            <section style={styles.section}>
              <h3 style={styles.sectionTitle}>承認設定</h3>
              <ul style={styles.list}>
                <li>国内承認: {member.domestic_approval_required ? 'あり' : 'なし'}</li>
                <li>海外承認: {member.foreign_approval_required ? 'あり' : 'なし'}</li>
              </ul>
            </section>

            <section style={styles.section}>
              <h3 style={styles.sectionTitle}>チケット関連</h3>
              <ul style={styles.list}>
                <li>
                  マイレージ登録内容:
                  {(member.mileage_numbers ?? []).length === 0 ? (
                    <span> なし</span>
                  ) : (
                    <ul>
                      {(member.mileage_numbers ?? []).map(m => (
                        <li key={m.id}>
                          {m.carrier}: {m.number}
                        </li>
                      ))}
                    </ul>
                  )}
                </li>
                <li>EXIC 会員情報: {member.exic_card?.exic_number || 'なし'}</li>
                <li>ビジネスえきねっと会員ID: {member.business_ekinet_account?.business_ekinet_id || 'なし'}</li>
              </ul>
            </section>

            <section style={styles.section}>
              <h3 style={styles.sectionTitle}>状態</h3>
              <ul style={styles.list}>
                <li>ユーザー状態: {member.disabled ? '無効' : '有効'}</li>
              </ul>
            </section>
          </>
        )}
      </ModalBody>
    </Modal>
  );
};

const getStyles = (_theme: AITTheme): { [key: string]: React.CSSProperties } => ({
  body: {
    padding: '16px 24px'
  },
  section: {
    marginBottom: 16
  },
  sectionTitle: {
    fontWeight: 'bold',
    marginBottom: 4,
    borderBottom: '1px solid #ccc',
    paddingBottom: 2
  },
  list: {
    margin: 0,
    paddingLeft: 20
  },
  emptyMessage: {
    padding: '1em 0',
    fontWeight: 'bold'
  },
  errorMessage: {
    padding: '1em 0',
    fontWeight: 'bold',
    color: 'red'
  }
});

export default MemberInfoModal;
